/************************************** THIRD-PARTY MODULES ***************************************/
import {observable, action, computed, autorun, extendObservable, IObservableArray} from 'mobx';
import {Int, isInt, singleton, Any, pushIfNew} from 'mad-utils/lib/shared';

/**************************************** PROJECT MODULES *****************************************/
import {Store} from '../root-store';
import {Mousetrap} from '@canimmunize/core-imports';

/**************************************** TYPE DEFINITIONS ****************************************/
interface HotkeyInfo {
    keyCombo: string;
    description: string;
    callback: (k?: ExtendedKeyboardEvent) => any;
}

/********************************************* EXPORT *********************************************/
/**
 * @export HotkeyStore - Store Hotkey data
 */
export class HotkeyStore {
    public static Instance: HotkeyStore | null = null;

    /***** PROPS *****/
    @observable
    public hotkeyInfo = [] as HotkeyInfo[];

    /***** INITIALIZATION *****/
    /**
     * @constructor
     * Factory method (true constructor)
     */
    public static new = (rootStore: Store): HotkeyStore => {
        if (!HotkeyStore.Instance) {
            HotkeyStore.Instance = new HotkeyStore(rootStore);
        }
        return HotkeyStore.Instance;
    };

    /***** ACTIONS *****/
    /**
     * Adds HotkeyInfo to list, globally bind hotkey
     * @param {Record<string, Function>} newAddHotkey {"ctrl-n": someMethod}
     */
    @action
    public addHotkey = (newHotkeyInfo: HotkeyInfo) => {
        if (this.hotkeyInfo.find(info => info.keyCombo === newHotkeyInfo.keyCombo)) return;
        this.hotkeyInfo.push(newHotkeyInfo);
        Mousetrap.bind(newHotkeyInfo.keyCombo, newHotkeyInfo.callback);
    };

    /**
     * Block all hotkeys from running (until this.unpause run)
     */
    public pauseHotkeys = () => Mousetrap.pause();

    /**
     * Shut off hotkey blocking (started by this.pause)
     */
    public unpauseHotkeys = () => Mousetrap.unpause();

    /**
     * Remove hotkey with matching index, globally unbind hotkey
     * @param {string} keyCombo Key combination to de-register
     */
    @action
    public removeHotkey = (keyCombo: string) => () => {
        const matchIdx = this.hotkeyInfo.findIndex(info => info.keyCombo === keyCombo);
        if (matchIdx !== -1) {
            this.hotkeyInfo.splice(matchIdx, 1);
            Mousetrap.unbind(keyCombo);
        }
    };

    private constructor(public rootStore: Store) {}
}
